<template>
  <dialog ref="sheet" class="cd-sheet" aria-labelledby="cd-title" @cancel.prevent="close" @close="$emit('close')">
    <header class="cd-head">
      <div>
        <h2 id="cd-title">{{ t("cd_h") }}</h2>
        <p class="cd-sub">{{ site.brand }}</p>
      </div>
      <button type="button" class="cd-x" :aria-label="t('nav_close')" @click="close"><span aria-hidden="true">✕</span></button>
    </header>

    <div class="cd-body">
      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="loadError" class="empty" role="alert">{{ loadError }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button></p>

      <template v-else>
        <p v-if="flash" class="cd-flash" role="status">{{ flash }}</p>
        <p v-if="state" class="cd-state" :class="`cd-s-${state}`">
          <span class="cd-dot" aria-hidden="true"></span>{{ stateLabel }}
        </p>
        <p class="cd-say">{{ stateSay }}</p>

        <!-- unbound: the only thing to do is name the host -->
        <form v-if="!state" class="cd-form" @submit.prevent="save">
          <label for="cd-host">{{ t("cd_host_label") }}</label>
          <input id="cd-host" v-model="host" type="text" inputmode="url" autocomplete="off" spellcheck="false" placeholder="geo.acme.com" :disabled="busy">
          <p class="cd-hint">{{ t("cd_host_hint") }}</p>
          <button type="submit" class="btn" :disabled="busy || !host.trim()">{{ busy ? t("cd_saving") : t("cd_save") }}</button>
        </form>

        <template v-else>
          <p class="cd-host-line"><strong>{{ panel.host }}</strong></p>

          <!-- The record to paste. Present on every state, including the failures: a panel that
               says "not pointing at us" without showing the record is a support ticket. -->
          <div v-if="state !== 'live'" class="cd-record">
            <span class="cd-rl">{{ t("cd_record") }}</span>
            <code>{{ panel.record }}</code>
            <button type="button" class="atbtn" @click="copyRecord">{{ copied ? t("ob_copied") : t("ob_copy") }}</button>
          </div>
          <p v-if="panel.resolved_to" class="cd-resolved">{{ t("cd_resolved", { value: panel.resolved_to }) }}</p>

          <a v-if="state === 'live'" class="cd-live" :href="`https://${panel.host}/`" target="_blank" rel="noopener">https://{{ panel.host }}/<span class="sr-only"> {{ t("opens_new") }}</span></a>

          <p v-if="actionError" class="cd-err" role="alert">{{ actionError }}</p>
          <p v-if="upgradeNeeded" class="cd-err" role="alert">
            {{ upgradeNeeded }} <router-link to="/account/billing">{{ t("cd_upgrade_cta") }}</router-link>
          </p>

          <div class="cd-acts">
            <button v-if="state !== 'live'" type="button" class="btn" :disabled="busy" @click="recheck">{{ busy ? t("cd_checking") : t("cd_verify") }}</button>
            <button v-else type="button" class="atbtn" :disabled="busy" @click="recheck">{{ busy ? t("cd_checking") : t("cd_recheck") }}</button>
            <button type="button" class="atbtn cd-remove" :disabled="busy" @click="confirming = true">{{ t("cd_remove") }}</button>
          </div>

          <!-- One sentence the client must read before unbinding: submitted URLs cannot be recalled. -->
          <div v-if="confirming" class="cd-confirm">
            <p>{{ panel.notice }}</p>
            <div class="cd-acts">
              <button type="button" class="btn" :disabled="busy" @click="remove">{{ busy ? t("cd_removing") : t("cd_remove_confirm") }}</button>
              <button type="button" class="atbtn" :disabled="busy" @click="confirming = false">{{ t("cd_cancel") }}</button>
            </div>
          </div>
        </template>
      </template>
    </div>
  </dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { getSiteDomain, recheckSiteDomain, removeSiteDomain, saveSiteDomain } from "../api/geoExecR9";
import { useI18n } from "../useI18n";

const props = defineProps({ site: { type: Object, required: true } });
defineEmits(["close"]);

const { t } = useI18n();
const sheet = ref(null);
const panel = ref({}), host = ref(""), loading = ref(true), busy = ref(false);
const loadError = ref(""), actionError = ref(""), upgradeNeeded = ref("");
const confirming = ref(false), copied = ref(false), flash = ref("");
let copyTimer;

const state = computed(() => String(panel.value.state || ""));
const stateLabel = computed(() => t({
  waiting_for_dns: "cd_st_waiting",
  dns_mismatch: "cd_st_mismatch",
  verified_issuing_cert: "cd_st_issuing",
  cert_failed: "cd_st_cert_failed",
  live: "cd_st_live",
}[state.value] || "cd_st_waiting"));
const stateSay = computed(() => {
  if (!state.value) return t("cd_say_unbound");
  if (state.value === "cert_failed") return panel.value.cert_error ? t("cd_say_cert_failed_why", { why: panel.value.cert_error }) : t("cd_say_cert_failed");
  return t({
    waiting_for_dns: "cd_say_waiting",
    dns_mismatch: "cd_say_mismatch",
    verified_issuing_cert: "cd_say_issuing",
    live: "cd_say_live",
  }[state.value] || "cd_say_waiting");
});

/** One English sentence per error the four routes can return. */
function explain(error) {
  const code = String(error?.payload?.error || "");
  if (error?.status === 402) { upgradeNeeded.value = error?.payload?.message || t("cd_e_autopilot"); return ""; }
  if (code === "host_taken") return t("cd_e_host_taken");
  if (code === "invalid_host") return t("cd_e_invalid_host");
  if (code === "our_own_suffix") return t("cd_e_our_suffix");
  if (code === "host_required") return t("cd_e_host_required");
  if (code === "dns_not_pointing") {
    const reason = String(error?.payload?.reason || "");
    if (reason === "wrong_target") return t("cd_e_wrong_target", { value: error.payload.resolved_to || "—", target: error.payload.expected || "" });
    if (reason === "no_record") return t("cd_e_no_record");
    return t("cd_e_dns_unresolved");
  }
  if (code === "cert_failed") return t("cd_e_cert_failed");
  if (code === "republish_failed") return t("cd_e_republish_failed");
  if (code === "domain_not_found" || code === "site_not_found") return t("cd_e_gone");
  return error?.message || t("cd_e_generic");
}

/** A failed write still carries the panel, so the state machine keeps moving on screen. */
function absorb(error) {
  actionError.value = explain(error);
  if (error?.payload?.state !== undefined) panel.value = error.payload;
}

async function load() {
  loading.value = true;
  loadError.value = "";
  try {
    panel.value = await getSiteDomain(props.site.site_id);
  } catch (error) {
    loadError.value = error?.message || t("cd_e_generic");
  } finally {
    loading.value = false;
  }
}

async function run(action) {
  busy.value = true;
  actionError.value = "";
  flash.value = "";
  upgradeNeeded.value = "";
  try {
    panel.value = await action();
    confirming.value = false;
  } catch (error) {
    absorb(error);
  } finally {
    busy.value = false;
  }
}

const save = () => run(async () => saveSiteDomain(props.site.site_id, host.value.trim()));
const recheck = () => run(async () => recheckSiteDomain(props.site.site_id));
const remove = () => run(async () => {
  const gone = panel.value.host;
  const result = await removeSiteDomain(props.site.site_id);
  host.value = "";
  flash.value = t("cd_removed", { host: gone, url: result.base_url || "" });
  return { notice: result.notice };  // back to unbound, keeping the sentence about submitted URLs
});

async function copyRecord() {
  const value = String(panel.value.record || "");
  if (!value) return;
  try {
    if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
    await navigator.clipboard.writeText(value);
  } catch {
    const input = document.createElement("textarea");
    input.value = value; input.setAttribute("readonly", ""); input.style.position = "fixed"; input.style.opacity = "0";
    document.body.append(input); input.select(); document.execCommand("copy"); input.remove();
  }
  copied.value = true;
  clearTimeout(copyTimer);
  copyTimer = setTimeout(() => { copied.value = false; }, 1500);
}

function close() {
  sheet.value?.close();
}

onMounted(() => {
  sheet.value?.showModal();
  load();
});
onBeforeUnmount(() => clearTimeout(copyTimer));
</script>

<style scoped>
/* A right-hand sheet, not a centred box: the site card stays where it was, and the panel is one
   more layer on the same surface. `dialog` rather than a div so Escape, the backdrop and the focus
   trap are the platform's job. */
.cd-sheet {
  position: fixed;
  inset: 0 0 0 auto;
  width: min(460px, 100vw);
  max-width: 100vw;
  height: 100dvh;
  max-height: 100dvh;
  margin: 0;
  padding: 0;
  border: none;
  border-left: 1px solid var(--line);
  background: var(--card);
  color: var(--ink);
  box-shadow: -18px 0 44px -26px rgba(26, 35, 51, .34);
  display: flex;
  flex-direction: column;
}
.cd-sheet[open] { animation: cd-in var(--dur) var(--ease-out); }
.cd-sheet::backdrop { background: rgba(26, 35, 51, .32); }
@keyframes cd-in { from { transform: translateX(100%); } to { transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .cd-sheet[open] { animation: none; }
}

.cd-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 22px 24px 14px; border-bottom: 1px solid var(--line); }
.cd-head h2 { font-size: var(--fs-large); letter-spacing: var(--ls-title); }
.cd-sub { font-size: var(--fs-caption); color: var(--mute); margin-top: 2px; }
.cd-x { border: none; background: none; color: var(--mute); font-size: 18px; line-height: 1; padding: 6px; cursor: pointer; border-radius: 8px; }
.cd-x:hover { color: var(--ink); background: var(--ink-hover); }
.cd-x:focus-visible, .cd-sheet :focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }

.cd-body { padding: 20px 24px 28px; overflow-y: auto; display: grid; gap: 14px; align-content: start; }
.cd-state { display: inline-flex; align-items: center; gap: 7px; font-size: var(--fs-caption2); font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--mute); }
.cd-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.cd-s-live, .cd-live { color: var(--jade); }
.cd-s-waiting_for_dns, .cd-s-verified_issuing_cert { color: var(--amber); }
.cd-s-dns_mismatch, .cd-s-cert_failed { color: var(--verm); }
.cd-flash { font-size: var(--fs-caption); color: var(--jade); line-height: 1.55; }
.cd-say { font-size: var(--fs-subhead); line-height: 1.6; color: var(--body); }
.cd-host-line { font-size: var(--fs-body); font-weight: 600; word-break: break-all; }

.cd-form { display: grid; gap: 8px; }
.cd-form label { font-size: var(--fs-caption); color: var(--mute); }
.cd-form input { font-family: inherit; font-size: var(--fs-subhead); padding: 11px 13px; border: 1px solid var(--line); border-radius: 12px; background: var(--paper); color: var(--ink); }
.cd-form input:focus-visible { outline: 3px solid var(--indigo); outline-offset: 1px; }
.cd-hint { font-size: var(--fs-caption); color: var(--mute); line-height: 1.55; }
.cd-form .btn { justify-self: start; }

.cd-record { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; padding: 12px 14px; border: 1px solid var(--line); border-radius: 12px; background: var(--paper); }
.cd-rl { font-size: var(--fs-caption2); text-transform: uppercase; letter-spacing: .06em; color: var(--mute); }
.cd-record code { font-size: var(--fs-caption); word-break: break-all; }
.cd-resolved { font-size: var(--fs-caption); color: var(--mute); word-break: break-all; }
.cd-live { font-size: var(--fs-subhead); word-break: break-all; }
.cd-err { font-size: var(--fs-caption); color: var(--verm); line-height: 1.55; }
.cd-acts { display: flex; gap: 10px; flex-wrap: wrap; }
.cd-remove { color: var(--verm); }
.cd-confirm { display: grid; gap: 12px; padding: 14px; border: 1px solid var(--bad-line); border-radius: 12px; background: var(--bad-bg); }
.cd-confirm p { font-size: var(--fs-caption); line-height: 1.6; }
.cd-confirm .btn { background: var(--verm); }
.cd-confirm .btn:hover { filter: brightness(.93); }

.btn:active, .atbtn:active { transform: scale(.985); }
</style>
